import React, { useMemo } from 'react';
import Select, { StylesConfig, GroupBase } from 'react-select';
import { countries } from 'countries-list';

interface CountryOption {
  value: string;
  label: string;
}

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const countryOptions: CountryOption[] = Object.values(countries)
  .map(c => ({ value: c.name, label: c.name }))
  .sort((a, b) => a.label.localeCompare(b.label));

const selectStyles: StylesConfig<CountryOption, false, GroupBase<CountryOption>> = {
  control: (base, state) => ({
    ...base,
    width: '100%',
    minHeight: '3rem',
    padding: '0.25rem 0.125rem',
    background: state.isFocused
      ? 'var(--surface-primary)'
      : 'var(--surface-elevated)',
    border: `1px solid ${state.isFocused ? 'var(--accent-primary)' : 'var(--border-secondary)'}`,
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 124, 255, 0.1)' : 'none',
    color: 'var(--text-primary)',
    fontSize: '0.9375rem',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--accent-primary)' : 'var(--border-primary)',
      background: state.isFocused ? 'var(--surface-primary)' : 'var(--surface-hover)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 0.75rem',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--text-primary)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-tertiary)',
    opacity: 0.6,
  }),
  input: (base) => ({
    ...base,
    color: 'var(--text-primary)',
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--text-tertiary)',
    padding: '0 0.5rem',
    '&:hover': { color: 'var(--text-secondary)' },
  }),
  menu: (base) => ({
    ...base,
    background: 'var(--surface-elevated)',
    border: '1px solid var(--border-secondary)',
    borderRadius: '0.5rem',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: '0.25rem',
    maxHeight: '220px',
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? 'var(--accent-primary)'
      : state.isFocused
      ? 'var(--surface-hover)'
      : 'transparent',
    color: state.isSelected ? '#ffffff' : 'var(--text-primary)',
    borderRadius: '0.375rem',
    fontSize: '0.9375rem',
    cursor: 'pointer',
    padding: '0.5rem 0.75rem',
    '&:active': { background: 'var(--surface-hover)' },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
  }),
};

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  placeholder = 'Select a country...',
  disabled = false,
}) => {
  const selectedOption = useMemo(
    () => countryOptions.find(o => o.value === value) ?? null,
    [value]
  );

  return (
    <Select<CountryOption>
      options={countryOptions}
      value={selectedOption}
      onChange={(option) => onChange(option ? option.value : '')}
      placeholder={placeholder}
      isDisabled={disabled}
      styles={selectStyles}
      isSearchable
      noOptionsMessage={() => 'No country found'}
    />
  );
};

export default CountrySelect;
